import React, { useEffect, useState } from "react";
import { BarChart, BaseDataItem, Card } from "Components";
import { useMultiDayEventContext, Stats } from "contexts/MultiDayEventContext";
import styled from "styled-components";
import { DateTime, DurationLike, Interval } from "luxon";
import { Tooltip } from "./Tooltip";
import { useMobileMediaQuery } from "hooks";
import { CardTitle } from "../../Components/CardTitle";
import { Loader } from "./Loader";
import { formatNumber } from "Utils/format";

export type DataItem = BaseDataItem & Stats & { date: DateTime };

const INITIAL_STATS: Stats = {
  entries: 0,
  visits: 0,
  uniqueVisits: 0,
  clicks: 0,
};

const STATS_LABELS: Record<keyof Stats, string> = {
  entries: "Entries",
  visits: "Visits",
  uniqueVisits: "Unique Visits",
  clicks: "Clicks",
};

export const EntriesChart: React.FC = () => {
  const { startDate, endDate, multiDayReport, multiDayReportLoading } =
    useMultiDayEventContext();
  const [chartData, setChartData] = useState<DataItem[]>([]);
  const [stats, setStats] = useState<Stats>(INITIAL_STATS);
  const isMobile = useMobileMediaQuery();
  const daysRange = endDate.diff(startDate, "days").days;
  const isSameDay = startDate.hasSame(endDate, "day");
  const entries = multiDayReport?.entries || [];

  const formatDate = (time: DateTime) => time.toFormat("MM/dd/yyyy");

  const splitChartDataByInterval = ({
    splitBy,
    labelFormat,
    findInterval,
  }: {
    splitBy: DurationLike;
    labelFormat: string | ((interval: DateTime) => string);
    findInterval: (interval: DataItem, dataEntryDatetime: DateTime) => boolean;
  }) => {
    const newChartData: DataItem[] = [];
    let newStats: Stats = INITIAL_STATS;

    const intervals = Interval.fromDateTimes(startDate, endDate)
      .splitBy(splitBy)
      .map((d) => d.start);

    for (const interval of intervals) {
      newChartData.push({
        label:
          typeof labelFormat === "string"
            ? interval.toFormat(labelFormat)
            : labelFormat(interval),
        date: interval,
        entries: 0,
        visits: 0,
        uniqueVisits: 0,
        clicks: 0,
      });
    }

    const addToStats = (item: Stats, stats: Stats) => {
      return {
        entries: item.entries + stats.entries,
        visits: item.visits + stats.visits,
        uniqueVisits: item.uniqueVisits + stats.uniqueVisits,
        clicks: item.clicks + stats.clicks,
      };
    };

    for (const dataEntry of entries) {
      const datetime = DateTime.fromISO(dataEntry.datetime);
      const charDataItemIndex = newChartData.findIndex((item) =>
        findInterval(item, datetime),
      );

      if (charDataItemIndex >= 0) {
        const item = newChartData[charDataItemIndex];
        newChartData[charDataItemIndex] = {
          ...item,
          ...addToStats(item, dataEntry),
        };

        newStats = addToStats(newStats, dataEntry);
      }
    }

    setChartData(newChartData);
    setStats(newStats);
  };

  const buildChartData = () => {
    if (isSameDay) {
      splitChartDataByInterval({
        splitBy: { hours: 1 },
        labelFormat: (interval) =>
          interval.toFormat("ha").toLowerCase().replace("m", ""),
        findInterval: (item, datetime) =>
          datetime >= item.date &&
          datetime.diff(item.date, "minutes").minutes < 60,
      });
    } else {
      splitChartDataByInterval({
        splitBy: { days: 1 },
        labelFormat: daysRange <= 7 ? "EEE MM/dd" : "MMM dd",
        findInterval: (item, datetime) => item.date.hasSame(datetime, "day"),
      });
    }
  };

  useEffect(() => {
    buildChartData();
  }, [entries?.length]);

  return (
    <StyledCard isElevated>
      {multiDayReportLoading ? (
        <Loader />
      ) : (
        <>
          <CardTitle>
            Entries{" "}
            {isSameDay ? (
              `on ${formatDate(startDate)}`
            ) : (
              <>
                from {isMobile ? <br /> : " "}
                {`${formatDate(startDate)} - ${formatDate(endDate)}`}
              </>
            )}
          </CardTitle>
          <Chart>
            <BarChart<DataItem>
              height={
                isMobile
                  ? isSameDay
                    ? 700
                    : Math.min(daysRange * 50, 500)
                  : 400
              }
              data={chartData}
              dataKeys={{
                entries: {
                  type: "bar",
                },
              }}
              CustomTooltip={Tooltip}
              {...(isMobile && isSameDay && { mainAxisTickMargin: 30 })}
            />
          </Chart>
          <StatsContainer>
            {Object.keys(INITIAL_STATS).map((key) => (
              <Stat key={key}>
                <StatValue>{formatNumber(stats[key])}</StatValue>
                <StatLabel>{STATS_LABELS[key]}</StatLabel>
              </Stat>
            ))}
          </StatsContainer>
        </>
      )}
    </StyledCard>
  );
};

const StyledCard = styled(Card)`
  grid-column: 1 / -1;
  margin-top: 12px;

  ${({ theme }) => theme.media.mobile} {
    margin-top: 15px;
  }
`;

const Chart = styled.div`
  margin: 40px 0 30px;

  ${({ theme }) => theme.media.mobile} {
    margin: 20px 0 15px;
  }
`;

export const StatsContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin: 40px 40px 10px;

  ${({ theme }) => theme.media.mobile} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }
`;

export const Stat = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 5px;
`;

const StatValue = styled.span`
  color: var(--main-color);
  font-weight: 600;
  font-size: 26px;
`;

const StatLabel = styled.span`
  color: var(--secondary-text-color);
  font-size: 16px;

  ${({ theme }) => theme.media.mobile} {
    font-size: 14px;
  }
`;
